import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  ArrowLeft, Plus, MagnifyingGlass, Truck, Package, ClipboardText,
  Check, X, DownloadSimple, Eye, Warning, ArrowsLeftRight, FilePdf,
  UploadSimple, CaretDown, Clock, CheckCircle, XCircle, Minus,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtCurrency = (v) => (v || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const TABS = [
  { key: 'indents', label: 'Indents', icon: ClipboardText },
  { key: 'grn', label: 'GRN', icon: Package },
  { key: 'movements', label: 'Movement', icon: ArrowsLeftRight },
];

const ProcurementCenter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'indents');
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState(searchParams.get('project') || '');
  const [stats, setStats] = useState(null);
  const canSeeFinancials = ['super_admin', 'admin', 'project_lead'].includes(user?.role);

  useEffect(() => { fetchProjects(); }, []);
  useEffect(() => { fetchStats(); }, [filterProject]);
  useEffect(() => { setSearchParams({ tab: activeTab, ...(filterProject ? { project: filterProject } : {}) }); }, [activeTab, filterProject]);

  const fetchProjects = async () => {
    try { const { data } = await axios.get(`${BACKEND}/api/projects?page=1&limit=100`, { withCredentials: true }); setProjects(data.projects || data); } catch {}
  };
  const fetchStats = async () => {
    try { const params = filterProject ? `?project_id=${filterProject}` : ''; const { data } = await axios.get(`${BACKEND}/api/procurement/stats${params}`, { withCredentials: true }); setStats(data); } catch {}
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button onClick={() => navigate('/dashboard')} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100 mb-3" data-testid="back-button">
            <ArrowLeft size={20} className="mr-2" /> Back
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Procurement Control</h1>
          <p className="text-sm text-zinc-600 mt-1">Indent → Quotation → PO → GRN → Movement</p>
          <Button onClick={() => navigate('/procurement/reconciliation')} variant="outline" className="border-blue-300 text-blue-700 rounded-none hover:bg-blue-50 mt-3 text-xs" data-testid="go-reconciliation-btn">
            Reconciliation & Control View
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Project filter */}
        <div className="flex items-center gap-3 mb-5">
          <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="bg-white border border-zinc-200 rounded-none p-2.5 text-sm min-w-[220px]" data-testid="project-filter">
            <option value="">All Projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.pi_number} — {p.client_name}</option>)}
          </select>
          {stats && (
            <div className="flex gap-3 flex-1">
              <StatCard label="Indents" value={stats.indents.total} sub={`${stats.indents.open} open`} />
              <StatCard label="POs" value={stats.purchase_orders.total} />
              <StatCard label="GRNs" value={stats.grn.total} sub={stats.grn.with_deviation > 0 ? `${stats.grn.with_deviation} deviations` : ''} color={stats.grn.with_deviation > 0 ? 'text-red-600' : ''} />
              <StatCard label="Movements" value={stats.movements.total} />
              {stats.pending_mapping > 0 && <StatCard label="Mapping Pending" value={stats.pending_mapping} color="text-amber-600" />}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-200 mb-6">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === t.key ? 'border-blue-700 text-blue-700' : 'border-transparent text-zinc-500 hover:text-zinc-700'}`} data-testid={`tab-${t.key}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'indents' && <IndentTab projectId={filterProject} projects={projects} canSeeFinancials={canSeeFinancials} user={user} onRefresh={fetchStats} />}
        {activeTab === 'grn' && <GRNTab projectId={filterProject} canSeeFinancials={canSeeFinancials} user={user} onRefresh={fetchStats} />}
        {activeTab === 'movements' && <MovementTab projectId={filterProject} projects={projects} user={user} onRefresh={fetchStats} />}
      </main>
    </div>
  );
};

const StatCard = ({ label, value, sub, color = '' }) => (
  <div className="bg-white border border-zinc-200 px-3 py-2 flex-1 min-w-0">
    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{label}</p>
    <p className={`text-lg font-bold ${color || 'text-zinc-900'}`}>{value}</p>
    {sub && <p className="text-[10px] text-zinc-400">{sub}</p>}
  </div>
);

// ========== INDENT TAB ==========
const IndentTab = ({ projectId, projects, canSeeFinancials, user, onRefresh }) => {
  const [indents, setIndents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ project_id: '', trade_id: '', material_id: '', description: '', required_quantity: '', unit: 'Nos', required_date: '', priority: 'normal', remarks: '' });
  const [trades, setTrades] = useState([]);
  const [quotDialogOpen, setQuotDialogOpen] = useState(false);
  const [activeIndent, setActiveIndent] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [quotForm, setQuotForm] = useState({ supplier_name: '', quoted_unit_rate: '', remarks: '' });
  const quotFileRef = useRef(null);

  useEffect(() => { fetchIndents(); fetchTrades(); }, [projectId]);

  const fetchIndents = async () => {
    try { const params = projectId ? `?project_id=${projectId}` : ''; const { data } = await axios.get(`${BACKEND}/api/indents${params}`, { withCredentials: true }); setIndents(data); } catch {} finally { setLoading(false); }
  };
  const fetchTrades = async () => { try { const { data } = await axios.get(`${BACKEND}/api/trades`, { withCredentials: true }); setTrades(data); } catch {} };

  const handleCreate = async () => {
    if (!form.description || !form.required_quantity || !form.required_date || !form.project_id) { toast.error('Fill required fields'); return; }
    try {
      await axios.post(`${BACKEND}/api/indents`, form, { withCredentials: true });
      toast.success('Indent created'); setDialogOpen(false);
      setForm({ project_id: '', trade_id: '', material_id: '', description: '', required_quantity: '', unit: 'Nos', required_date: '', priority: 'normal', remarks: '' });
      fetchIndents(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openQuotations = async (indent) => {
    setActiveIndent(indent);
    try { const { data } = await axios.get(`${BACKEND}/api/indents/${indent.id}/quotations`, { withCredentials: true }); setQuotations(data); } catch { setQuotations([]); }
    setQuotDialogOpen(true);
  };

  const handleUploadQuotation = async () => {
    if (!quotForm.supplier_name || !quotForm.quoted_unit_rate) { toast.error('Supplier and unit rate required'); return; }
    const qty = parseFloat(activeIndent?.required_quantity || 0);
    const unitRate = parseFloat(quotForm.quoted_unit_rate) || 0;
    const total = +(unitRate * qty).toFixed(2);
    const fd = new FormData();
    fd.append('supplier_name', quotForm.supplier_name);
    fd.append('quoted_unit_rate', unitRate);
    fd.append('quoted_amount', total);
    fd.append('remarks', quotForm.remarks);
    const file = quotFileRef.current?.files?.[0];
    if (file) fd.append('document', file);
    try {
      await axios.post(`${BACKEND}/api/indents/${activeIndent.id}/quotations`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Quotation uploaded');
      setQuotForm({ supplier_name: '', quoted_unit_rate: '', remarks: '' });
      if (quotFileRef.current) quotFileRef.current.value = '';
      const { data } = await axios.get(`${BACKEND}/api/indents/${activeIndent.id}/quotations`, { withCredentials: true }); setQuotations(data);
      fetchIndents();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const priorityBadge = (p) => {
    const map = { critical: 'bg-red-100 text-red-700 border-red-200', high: 'bg-orange-100 text-orange-700 border-orange-200', normal: 'bg-blue-100 text-blue-700 border-blue-200', low: 'bg-zinc-100 text-zinc-600 border-zinc-200' };
    return map[p] || map.normal;
  };
  const statusBadge = (s) => {
    const map = {
      open: 'bg-amber-100 text-amber-700 border-amber-200',
      Draft: 'bg-zinc-100 text-zinc-600 border-zinc-200',
      Submitted: 'bg-blue-100 text-blue-700 border-blue-200',
      Approved: 'bg-green-100 text-green-700 border-green-200',
      Rejected: 'bg-red-100 text-red-700 border-red-200',
      quoted: 'bg-blue-100 text-blue-700 border-blue-200',
      po_created: 'bg-green-100 text-green-700 border-green-200',
      closed: 'bg-zinc-100 text-zinc-600 border-zinc-200',
    };
    return map[s] || map.open;
  };

  const isApprover = ['super_admin', 'admin'].includes(user?.role);

  const submitIndent = async (id) => {
    try { await axios.post(`${BACKEND}/api/indents/${id}/submit`, {}, { withCredentials: true }); toast.success('Indent submitted for approval'); fetchIndents(); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Submit failed'); }
  };
  const approveIndent = async (id) => {
    try { await axios.post(`${BACKEND}/api/indents/${id}/approve`, {}, { withCredentials: true }); toast.success('Indent approved'); fetchIndents(); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Approve failed'); }
  };
  const rejectIndent = async (id) => {
    const reason = window.prompt('Rejection reason:');
    if (reason === null) return;
    try { await axios.post(`${BACKEND}/api/indents/${id}/reject`, { reason }, { withCredentials: true }); toast.success('Indent rejected'); fetchIndents(); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Reject failed'); }
  };

  if (loading) return <div className="text-center py-8 text-zinc-500">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-zinc-800">Indents ({indents.length})</h2>
        <Button onClick={() => { setForm(f => ({ ...f, project_id: projectId || '' })); setDialogOpen(true); }} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="create-indent-btn"><Plus size={16} className="mr-1" /> New Indent</Button>
      </div>
      {indents.length === 0 ? (
        <div className="text-center py-12 bg-white border border-zinc-200" data-testid="empty-indents"><ClipboardText size={40} className="mx-auto text-zinc-300 mb-3" /><p className="text-zinc-500">No indents yet</p><p className="text-xs text-zinc-400 mt-1">Raise a material indent to start procurement</p></div>
      ) : (
        <div className="space-y-2">
          {indents.map(ind => (
            <div key={ind.id} className="bg-white border border-zinc-200 p-4 flex items-start gap-4" data-testid={`indent-${ind.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono font-bold text-blue-700">{ind.indent_number}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 border ${priorityBadge(ind.priority)}`}>{ind.priority.toUpperCase()}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 border ${statusBadge(ind.status)}`}>{ind.status.toUpperCase()}</span>
                </div>
                <p className="text-sm font-medium text-zinc-800 mt-1">{ind.description}</p>
                <div className="flex gap-4 mt-1 text-xs text-zinc-500">
                  <span>Qty: {ind.required_quantity} {ind.unit}</span>
                  <span>Need by: {fmtDate(ind.required_date)}</span>
                  <span>By: {ind.created_by_name}</span>
                  {ind.remarks && <span>Remark: {ind.remarks}</span>}
                </div>
              </div>
              <div className="flex flex-col gap-1.5 items-end shrink-0">
                <Button onClick={() => openQuotations(ind)} variant="outline" size="sm" className="rounded-none border-zinc-300 text-xs" data-testid={`view-quotations-${ind.id}`}>
                  <Eye size={14} className="mr-1" /> Quotations
                </Button>
                {(ind.status === 'Draft' || ind.status === 'open') && (
                  <Button onClick={() => submitIndent(ind.id)} size="sm" className="rounded-none bg-blue-600 hover:bg-blue-700 text-white text-xs h-7" data-testid={`submit-indent-${ind.id}`}>Submit for Approval</Button>
                )}
                {ind.status === 'Submitted' && isApprover && (
                  <div className="flex gap-1">
                    <Button onClick={() => approveIndent(ind.id)} size="sm" className="rounded-none bg-green-600 hover:bg-green-700 text-white text-xs h-7" data-testid={`approve-indent-${ind.id}`}><Check size={12} className="mr-0.5" /> Approve</Button>
                    <Button onClick={() => rejectIndent(ind.id)} size="sm" variant="outline" className="rounded-none border-red-300 text-red-600 text-xs h-7" data-testid={`reject-indent-${ind.id}`}><X size={12} className="mr-0.5" /> Reject</Button>
                  </div>
                )}
                {ind.status === 'Rejected' && ind.rejection_reason && (
                  <span className="text-[10px] text-red-600 max-w-[160px] truncate" title={ind.rejection_reason}>Reason: {ind.rejection_reason}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Indent Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-lg">
          <DialogHeader><DialogTitle className="text-lg font-bold">New Indent</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div>
              <Label className="text-xs font-bold uppercase text-zinc-500">Project *</Label>
              <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="indent-project">{!form.project_id && <option value="">Select</option>}{projects.map(p => <option key={p.id} value={p.id}>{p.pi_number} — {p.client_name}</option>)}</select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-zinc-500">Description *</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-none bg-zinc-50" placeholder="e.g. 18mm BWP Plywood for Kitchen" data-testid="indent-description" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase text-zinc-500">Qty *</Label>
                <Input type="number" value={form.required_quantity} onChange={(e) => setForm({ ...form, required_quantity: e.target.value })} className="mt-1 rounded-none bg-zinc-50" data-testid="indent-qty" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-zinc-500">Unit</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1 rounded-none bg-zinc-50" data-testid="indent-unit" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-zinc-500">Need By *</Label>
                <Input type="date" value={form.required_date} onChange={(e) => setForm({ ...form, required_date: e.target.value })} className="mt-1 rounded-none bg-zinc-50" data-testid="indent-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase text-zinc-500">Trade</Label>
                <select value={form.trade_id} onChange={(e) => setForm({ ...form, trade_id: e.target.value })} className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="indent-trade"><option value="">None</option>{trades.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-zinc-500">Priority</Label>
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="indent-priority"><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="critical">Critical</option></select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-zinc-500">Remarks</Label>
              <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="mt-1 rounded-none bg-zinc-50" data-testid="indent-remarks" />
            </div>
            <Button onClick={handleCreate} className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none py-5" data-testid="submit-indent">Create Indent</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quotations Dialog */}
      <Dialog open={quotDialogOpen} onOpenChange={setQuotDialogOpen}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg font-bold">Quotations — {activeIndent?.indent_number}</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500">{activeIndent?.description} · Qty: {activeIndent?.required_quantity} {activeIndent?.unit}</p>
          {/* Upload form */}
          <div className="border border-blue-200 bg-blue-50 p-3 mt-3 space-y-2">
            <p className="text-xs font-bold text-blue-800">Upload Supplier Quotation</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-zinc-500">Supplier Name *</Label>
                <Input value={quotForm.supplier_name} onChange={(e) => setQuotForm({ ...quotForm, supplier_name: e.target.value })} className="mt-1 rounded-none text-xs bg-white" data-testid="quot-supplier" />
              </div>
              <div>
                <Label className="text-[10px] text-zinc-500">Quoted Unit Rate *</Label>
                <Input type="number" value={quotForm.quoted_unit_rate} onChange={(e) => setQuotForm({ ...quotForm, quoted_unit_rate: e.target.value })} className="mt-1 rounded-none text-xs bg-white" data-testid="quot-unit-rate" placeholder={`per ${activeIndent?.unit || 'unit'}`} />
                <p className="text-[10px] text-zinc-400 mt-0.5" data-testid="quot-computed-total">
                  Total: {fmtCurrency((parseFloat(quotForm.quoted_unit_rate) || 0) * parseFloat(activeIndent?.required_quantity || 0))} ({activeIndent?.required_quantity} {activeIndent?.unit})
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-zinc-500">Remarks</Label>
                <Input value={quotForm.remarks} onChange={(e) => setQuotForm({ ...quotForm, remarks: e.target.value })} className="mt-1 rounded-none text-xs bg-white" data-testid="quot-remarks" />
              </div>
              <div>
                <Label className="text-[10px] text-zinc-500">Document (PDF/Image)</Label>
                <input ref={quotFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="mt-1 text-xs" data-testid="quot-file" />
              </div>
            </div>
            <Button onClick={handleUploadQuotation} size="sm" className="bg-blue-700 hover:bg-blue-800 text-white rounded-none text-xs" data-testid="upload-quotation-btn"><UploadSimple size={14} className="mr-1" /> Upload</Button>
          </div>
          {/* Existing quotations — vendor comparison */}
          <div className="mt-4 space-y-2">
            {quotations.length === 0 ? <p className="text-xs text-zinc-400">No quotations uploaded yet</p> : (() => {
              const rated = quotations.filter(q => (q.quoted_unit_rate || 0) > 0).map(q => q.quoted_unit_rate);
              const bestUnitRate = rated.length ? Math.min(...rated) : null;
              return quotations.map(q => {
                const isBest = bestUnitRate !== null && q.quoted_unit_rate === bestUnitRate;
                return (
                  <div key={q.id} className={`border p-3 ${isBest ? 'border-green-300 bg-green-50' : 'border-zinc-200 bg-zinc-50'}`} data-testid={`quotation-${q.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-zinc-800">{q.supplier_name}</p>
                          {isBest && <span className="text-[9px] bg-green-600 text-white px-1.5 py-0.5 rounded font-bold uppercase" data-testid={`best-rate-${q.id}`}>Best Unit Rate</span>}
                        </div>
                        {canSeeFinancials && (
                          <div className="flex items-center gap-3 mt-0.5">
                            <p className="text-xs text-blue-700 font-bold" data-testid={`quot-unit-rate-${q.id}`}>{(q.quoted_unit_rate || 0) > 0 ? `${fmtCurrency(q.quoted_unit_rate)} / ${activeIndent?.unit || 'unit'}` : '—'}</p>
                            <p className="text-xs text-zinc-600" data-testid={`quot-total-${q.id}`}>Total: {fmtCurrency(q.quoted_total_amount || q.quoted_amount)}</p>
                          </div>
                        )}
                        {q.remarks && <p className="text-xs text-zinc-500 mt-0.5">{q.remarks}</p>}
                        <p className="text-[10px] text-zinc-400 mt-1">Uploaded by {q.uploaded_by_name} · {fmtDate(q.created_at)}</p>
                      </div>
                      {q.document_url && <a href={`${BACKEND}${q.document_url}`} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline text-xs flex items-center gap-1"><DownloadSimple size={14} /> Doc</a>}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ========== GRN TAB ==========
const GRNTab = ({ projectId, canSeeFinancials, user, onRefresh }) => {
  const [grns, setGrns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [pos, setPOs] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [grnForm, setGrnForm] = useState({ delivery_date: new Date().toISOString().split('T')[0], invoice_number: '', invoice_amount: 0, advance_paid: false, items: [] });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailGRN, setDetailGRN] = useState(null);

  useEffect(() => { fetchGRNs(); fetchPOs(); }, [projectId]);

  const fetchGRNs = async () => {
    try { const params = projectId ? `?project_id=${projectId}` : ''; const { data } = await axios.get(`${BACKEND}/api/grn${params}`, { withCredentials: true }); setGrns(data); } catch {} finally { setLoading(false); }
  };
  const fetchPOs = async () => {
    if (!projectId) return;
    try { const { data } = await axios.get(`${BACKEND}/api/projects/${projectId}/purchase-orders`, { withCredentials: true }); setPOs(data); } catch {}
  };

  const selectPOForGRN = (po) => {
    setSelectedPO(po);
    setGrnForm({
      delivery_date: new Date().toISOString().split('T')[0], invoice_number: '', invoice_amount: 0, advance_paid: false,
      items: (po.line_items || []).map(li => ({
        description: li.description || li.item_description || '',
        ordered_quantity: li.quantity || 0,
        received_quantity: li.quantity || 0,
        unit: li.unit || 'Nos',
        status: 'accepted',
        material_match: true,
        gst_applicable: true,
        remarks: '',
        material_id: li.material_id || '',
      })),
    });
    setCreateOpen(true);
  };

  const updateGRNItem = (idx, field, value) => {
    const items = [...grnForm.items];
    items[idx] = { ...items[idx], [field]: value };
    setGrnForm({ ...grnForm, items });
  };

  const handleSubmitGRN = async () => {
    if (!selectedPO) { toast.error('Select a PO first'); return; }
    const missingRemarks = grnForm.items.some(i => i.received_quantity !== i.ordered_quantity && !i.remarks);
    if (missingRemarks) { toast.error('Remarks required for quantity mismatches'); return; }
    try {
      await axios.post(`${BACKEND}/api/grn`, { po_id: selectedPO.id, ...grnForm }, { withCredentials: true });
      toast.success('GRN submitted'); setCreateOpen(false); setSelectedPO(null); fetchGRNs(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openDetail = async (grn) => {
    try { const { data } = await axios.get(`${BACKEND}/api/grn/${grn.id}`, { withCredentials: true }); setDetailGRN(data); setDetailOpen(true); } catch { toast.error('Failed to load GRN'); }
  };

  const downloadPDF = async (grn) => {
    try {
      const response = await axios.get(`${BACKEND}/api/grn/${grn.id}/export/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', `GRN_${grn.grn_number}.pdf`);
      document.body.appendChild(link); link.click(); link.remove(); toast.success('PDF downloaded');
    } catch { toast.error('Failed to export PDF'); }
  };

  const linkPayment = async (grn) => {
    try {
      await axios.post(`${BACKEND}/api/grn/${grn.id}/payment-link`, { payable_amount: grn.invoice_amount }, { withCredentials: true });
      toast.success('Payment linked to GRN'); fetchGRNs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="text-center py-8 text-zinc-500">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-zinc-800">Goods Received Notes ({grns.length})</h2>
        {projectId && pos.length > 0 && (
          <div className="flex items-center gap-2">
            <select onChange={(e) => { const po = pos.find(p => p.id === e.target.value); if (po) selectPOForGRN(po); }} className="bg-white border border-zinc-200 rounded-none p-2 text-sm" data-testid="select-po-for-grn">
              <option value="">Select PO for GRN...</option>
              {pos.map(po => <option key={po.id} value={po.id}>{po.po_number} — {po.supplier_name}</option>)}
            </select>
          </div>
        )}
      </div>
      {!projectId && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-3 mb-4">Select a project to create GRN entries.</p>}
      {grns.length === 0 ? (
        <div className="text-center py-12 bg-white border border-zinc-200" data-testid="empty-grns"><Package size={40} className="mx-auto text-zinc-300 mb-3" /><p className="text-zinc-500">No GRN entries yet</p><p className="text-xs text-zinc-400 mt-1">Record material receipt when goods arrive at site</p></div>
      ) : (
        <div className="space-y-2">
          {grns.map(grn => (
            <div key={grn.id} className="bg-white border border-zinc-200 p-4" data-testid={`grn-${grn.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono font-bold text-green-700">{grn.grn_number}</span>
                    <span className="text-xs text-zinc-500">PO: {grn.po_number}</span>
                    {grn.has_deviation && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-red-100 text-red-700 border border-red-200">DEVIATION</span>}
                    {grn.has_short_supply && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-amber-100 text-amber-700 border border-amber-200">SHORT</span>}
                    {grn.has_excess && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-blue-100 text-blue-700 border border-blue-200">EXCESS</span>}
                    {grn.payment_linked && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-green-100 text-green-700 border border-green-200">PAYMENT LINKED</span>}
                  </div>
                  <p className="text-xs text-zinc-600 mt-1">{grn.supplier_name} · {grn.items?.length || 0} items · {fmtDate(grn.delivery_date)}</p>
                  {canSeeFinancials && <p className="text-xs text-zinc-500 mt-0.5">Invoice: {grn.invoice_number || '-'} · {fmtCurrency(grn.invoice_amount)}</p>}
                </div>
                <div className="flex gap-1">
                  <Button onClick={() => openDetail(grn)} variant="outline" size="sm" className="rounded-none border-zinc-300 text-xs h-7"><Eye size={14} /></Button>
                  <Button onClick={() => downloadPDF(grn)} variant="outline" size="sm" className="rounded-none border-zinc-300 text-xs h-7"><FilePdf size={14} /></Button>
                  {canSeeFinancials && !grn.payment_linked && <Button onClick={() => linkPayment(grn)} variant="outline" size="sm" className="rounded-none border-green-300 text-green-700 text-xs h-7" data-testid={`link-payment-${grn.id}`}>Link Pay</Button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create GRN Dialog (Checklist Style) */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg font-bold">GRN — {selectedPO?.po_number}</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500">Supplier: {selectedPO?.supplier_name} · {grnForm.items.length} items</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            <div>
              <Label className="text-[10px] text-zinc-500">Delivery Date</Label>
              <Input type="date" value={grnForm.delivery_date} onChange={(e) => setGrnForm({ ...grnForm, delivery_date: e.target.value })} className="mt-1 rounded-none text-xs bg-zinc-50" data-testid="grn-delivery-date" />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">Invoice Number</Label>
              <Input value={grnForm.invoice_number} onChange={(e) => setGrnForm({ ...grnForm, invoice_number: e.target.value })} className="mt-1 rounded-none text-xs bg-zinc-50" data-testid="grn-invoice-number" />
            </div>
            {canSeeFinancials && (
              <div>
                <Label className="text-[10px] text-zinc-500">Invoice Amount</Label>
                <Input type="number" value={grnForm.invoice_amount} onChange={(e) => setGrnForm({ ...grnForm, invoice_amount: parseFloat(e.target.value) || 0 })} className="mt-1 rounded-none text-xs bg-zinc-50" data-testid="grn-invoice-amount" />
              </div>
            )}
            <div className="flex items-end gap-2 pb-1">
              <label className="flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer">
                <input type="checkbox" checked={grnForm.advance_paid} onChange={(e) => setGrnForm({ ...grnForm, advance_paid: e.target.checked })} className="rounded border-zinc-300 h-3.5 w-3.5" data-testid="grn-advance-paid" />
                Advance Paid
              </label>
            </div>
          </div>

          {/* Checklist Items */}
          <div className="mt-4 space-y-3">
            {grnForm.items.map((item, idx) => {
              const qtyMatch = item.received_quantity === item.ordered_quantity;
              const isShort = item.received_quantity < item.ordered_quantity;
              return (
                <div key={idx} className={`border p-3 ${item.status === 'rejected' ? 'border-red-200 bg-red-50' : !qtyMatch ? 'border-amber-200 bg-amber-50' : 'border-zinc-200 bg-white'}`} data-testid={`grn-item-${idx}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="text-sm font-medium text-zinc-800">{item.description}</p>
                      <p className="text-xs text-zinc-500">Ordered: {item.ordered_quantity} {item.unit}</p>
                    </div>
                    <div className="flex gap-1">
                      {['accepted', 'partially_accepted', 'rejected'].map(s => (
                        <button key={s} onClick={() => updateGRNItem(idx, 'status', s)} className={`text-[10px] font-bold px-2 py-1 border ${item.status === s ? (s === 'accepted' ? 'bg-green-700 text-white border-green-700' : s === 'rejected' ? 'bg-red-700 text-white border-red-700' : 'bg-amber-600 text-white border-amber-600') : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`} data-testid={`grn-status-${idx}-${s}`}>
                          {s === 'accepted' ? 'Accept' : s === 'rejected' ? 'Reject' : 'Partial'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
                    <div>
                      <Label className="text-[10px] text-zinc-500">Received Qty</Label>
                      <div className="flex items-center gap-1 mt-1">
                        <button onClick={() => updateGRNItem(idx, 'received_quantity', Math.max(0, item.received_quantity - 1))} className="p-1 border border-zinc-200 bg-white hover:bg-zinc-50"><Minus size={12} /></button>
                        <Input type="number" value={item.received_quantity} onChange={(e) => updateGRNItem(idx, 'received_quantity', parseFloat(e.target.value) || 0)} className="rounded-none text-xs text-center w-16 p-1" data-testid={`grn-recv-qty-${idx}`} />
                        <button onClick={() => updateGRNItem(idx, 'received_quantity', item.received_quantity + 1)} className="p-1 border border-zinc-200 bg-white hover:bg-zinc-50"><Plus size={12} /></button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px] text-zinc-500">Material Match?</Label>
                      <div className="flex gap-1 mt-1">
                        <button onClick={() => updateGRNItem(idx, 'material_match', true)} className={`text-[10px] px-2 py-1 border ${item.material_match ? 'bg-green-700 text-white border-green-700' : 'bg-white text-zinc-600 border-zinc-200'}`} data-testid={`grn-match-yes-${idx}`}>Yes</button>
                        <button onClick={() => updateGRNItem(idx, 'material_match', false)} className={`text-[10px] px-2 py-1 border ${!item.material_match ? 'bg-red-700 text-white border-red-700' : 'bg-white text-zinc-600 border-zinc-200'}`} data-testid={`grn-match-no-${idx}`}>No</button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px] text-zinc-500">GST Applicable?</Label>
                      <div className="flex gap-1 mt-1">
                        <button onClick={() => updateGRNItem(idx, 'gst_applicable', true)} className={`text-[10px] px-2 py-1 border ${item.gst_applicable ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-zinc-600 border-zinc-200'}`} data-testid={`grn-gst-yes-${idx}`}>Yes</button>
                        <button onClick={() => updateGRNItem(idx, 'gst_applicable', false)} className={`text-[10px] px-2 py-1 border ${!item.gst_applicable ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-zinc-600 border-zinc-200'}`} data-testid={`grn-gst-no-${idx}`}>No</button>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] text-zinc-500">Remarks {!qtyMatch ? '*' : ''}</Label>
                      <Input value={item.remarks} onChange={(e) => updateGRNItem(idx, 'remarks', e.target.value)} placeholder={!qtyMatch ? 'Required for mismatch' : ''} className={`mt-1 rounded-none text-xs ${!qtyMatch && !item.remarks ? 'border-red-300 bg-red-50' : 'bg-zinc-50'}`} data-testid={`grn-remarks-${idx}`} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <Button onClick={handleSubmitGRN} className="w-full bg-green-700 hover:bg-green-800 text-white rounded-none py-5 mt-4" data-testid="submit-grn">Submit GRN</Button>
        </DialogContent>
      </Dialog>

      {/* GRN Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-3xl max-h-[85vh] overflow-y-auto">
          {detailGRN && (
            <>
              <DialogHeader><DialogTitle className="text-lg font-bold">{detailGRN.grn_number}</DialogTitle></DialogHeader>
              <div className="flex gap-4 mt-2 text-xs text-zinc-600">
                <span>PO: {detailGRN.po_number}</span><span>Supplier: {detailGRN.supplier_name}</span><span>Date: {fmtDate(detailGRN.delivery_date)}</span>
                {canSeeFinancials && <span>Invoice: {detailGRN.invoice_number} · {fmtCurrency(detailGRN.invoice_amount)}</span>}
              </div>
              <div className="flex gap-2 mt-2">
                {detailGRN.has_deviation && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-red-100 text-red-700 border border-red-200">MATERIAL DEVIATION</span>}
                {detailGRN.has_short_supply && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-amber-100 text-amber-700 border border-amber-200">SHORT SUPPLY</span>}
                {detailGRN.has_excess && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-blue-100 text-blue-700 border border-blue-200">EXCESS SUPPLY</span>}
                {detailGRN.advance_paid && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-purple-100 text-purple-700 border border-purple-200">ADVANCE PAID</span>}
              </div>
              <div className="mt-4 space-y-2">
                {(detailGRN.items || []).map((item, idx) => (
                  <div key={idx} className={`border p-3 ${item.status === 'rejected' ? 'border-red-200 bg-red-50' : item.status === 'partially_accepted' ? 'border-amber-200 bg-amber-50' : 'border-zinc-200 bg-zinc-50'}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium text-zinc-800">{item.description}</p>
                        <div className="flex gap-3 text-xs text-zinc-500 mt-1">
                          <span>Ordered: {item.ordered_quantity}</span>
                          <span className="font-bold text-zinc-800">Received: {item.received_quantity}</span>
                          <span>{item.unit}</span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 border ${item.status === 'accepted' ? 'bg-green-100 text-green-700 border-green-200' : item.status === 'rejected' ? 'bg-red-100 text-red-700 border-red-200' : 'bg-amber-100 text-amber-700 border-amber-200'}`}>{item.status.replace('_', ' ').toUpperCase()}</span>
                        {!item.material_match && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-red-100 text-red-700 border border-red-200">DEVIATION</span>}
                        {!item.gst_applicable && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-amber-100 text-amber-700 border border-amber-200">NON-GST</span>}
                      </div>
                    </div>
                    {item.remarks && <p className="text-xs text-zinc-600 mt-1 italic">"{item.remarks}"</p>}
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ========== MOVEMENT TAB ==========
const MovementTab = ({ projectId, projects, user, onRefresh }) => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ movement_type: 'site_to_site', from_location: '', to_location: '', project_id: '', description: '', quantity: '', unit: 'Nos', reason: '' });

  useEffect(() => { fetchMovements(); }, [projectId]);

  const fetchMovements = async () => {
    try { const params = projectId ? `?project_id=${projectId}` : ''; const { data } = await axios.get(`${BACKEND}/api/material-movements${params}`, { withCredentials: true }); setMovements(data); } catch {} finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.description || !form.quantity || !form.project_id) { toast.error('Fill required fields'); return; }
    try {
      await axios.post(`${BACKEND}/api/material-movements`, form, { withCredentials: true });
      toast.success('Movement created'); setDialogOpen(false);
      setForm({ movement_type: 'site_to_site', from_location: '', to_location: '', project_id: '', description: '', quantity: '', unit: 'Nos', reason: '' });
      fetchMovements(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const updateStatus = async (movId, status) => {
    try {
      await axios.put(`${BACKEND}/api/material-movements/${movId}/status`, { status }, { withCredentials: true });
      toast.success('Status updated'); fetchMovements();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const TYPES = [
    { value: 'site_to_site', label: 'Site → Site' },
    { value: 'site_to_factory', label: 'Site → Factory Return' },
    { value: 'site_to_supplier', label: 'Site → Supplier Return' },
    { value: 'factory_to_site', label: 'Factory → Site Issue' },
  ];
  const typeLabel = (t) => TYPES.find(x => x.value === t)?.label || t;
  const statusStyle = (s) => {
    const map = { initiated: 'bg-amber-100 text-amber-700 border-amber-200', in_transit: 'bg-blue-100 text-blue-700 border-blue-200', completed: 'bg-green-100 text-green-700 border-green-200' };
    return map[s] || map.initiated;
  };

  if (loading) return <div className="text-center py-8 text-zinc-500">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-zinc-800">Material Movement ({movements.length})</h2>
        <Button onClick={() => { setForm(f => ({ ...f, project_id: projectId || '' })); setDialogOpen(true); }} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="create-movement-btn"><Plus size={16} className="mr-1" /> New Movement</Button>
      </div>
      {movements.length === 0 ? (
        <div className="text-center py-12 bg-white border border-zinc-200" data-testid="empty-movements"><ArrowsLeftRight size={40} className="mx-auto text-zinc-300 mb-3" /><p className="text-zinc-500">No material movements yet</p><p className="text-xs text-zinc-400 mt-1">Log material transfers, returns, or wastage here</p></div>
      ) : (
        <div className="space-y-2">
          {movements.map(mov => (
            <div key={mov.id} className="bg-white border border-zinc-200 p-4 flex items-start gap-4" data-testid={`movement-${mov.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono font-bold text-blue-700">{mov.movement_number}</span>
                  <span className="text-[10px] bg-zinc-100 text-zinc-700 px-1.5 py-0.5 border border-zinc-200">{typeLabel(mov.movement_type)}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 border ${statusStyle(mov.status)}`}>{mov.status.replace('_', ' ').toUpperCase()}</span>
                </div>
                <p className="text-sm font-medium text-zinc-800 mt-1">{mov.description}</p>
                <div className="flex gap-4 text-xs text-zinc-500 mt-1">
                  <span>Qty: {mov.quantity} {mov.unit}</span>
                  {mov.from_location && <span>From: {mov.from_location}</span>}
                  {mov.to_location && <span>To: {mov.to_location}</span>}
                  {mov.reason && <span>Reason: {mov.reason}</span>}
                </div>
              </div>
              {mov.status !== 'completed' && (
                <div className="flex gap-1">
                  {mov.status === 'initiated' && <Button onClick={() => updateStatus(mov.id, 'in_transit')} variant="outline" size="sm" className="rounded-none border-blue-300 text-blue-700 text-xs h-7" data-testid={`transit-${mov.id}`}>In Transit</Button>}
                  {mov.status === 'in_transit' && <Button onClick={() => updateStatus(mov.id, 'completed')} variant="outline" size="sm" className="rounded-none border-green-300 text-green-700 text-xs h-7" data-testid={`complete-${mov.id}`}>Complete</Button>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Movement Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-lg">
          <DialogHeader><DialogTitle className="text-lg font-bold">New Material Movement</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div>
              <Label className="text-xs font-bold uppercase text-zinc-500">Project *</Label>
              <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="mov-project">{!form.project_id && <option value="">Select</option>}{projects.map(p => <option key={p.id} value={p.id}>{p.pi_number} — {p.client_name}</option>)}</select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-zinc-500">Movement Type *</Label>
              <select value={form.movement_type} onChange={(e) => setForm({ ...form, movement_type: e.target.value })} className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="mov-type">{TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase text-zinc-500">From Location</Label><Input value={form.from_location} onChange={(e) => setForm({ ...form, from_location: e.target.value })} className="mt-1 rounded-none bg-zinc-50" placeholder="e.g. Site A" data-testid="mov-from" /></div>
              <div><Label className="text-xs font-bold uppercase text-zinc-500">To Location</Label><Input value={form.to_location} onChange={(e) => setForm({ ...form, to_location: e.target.value })} className="mt-1 rounded-none bg-zinc-50" placeholder="e.g. Site B" data-testid="mov-to" /></div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-zinc-500">Description *</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-none bg-zinc-50" placeholder="Material description" data-testid="mov-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase text-zinc-500">Quantity *</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1 rounded-none bg-zinc-50" data-testid="mov-qty" /></div>
              <div><Label className="text-xs font-bold uppercase text-zinc-500">Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1 rounded-none bg-zinc-50" data-testid="mov-unit" /></div>
            </div>
            <div><Label className="text-xs font-bold uppercase text-zinc-500">Reason</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1 rounded-none bg-zinc-50" data-testid="mov-reason" /></div>
            <Button onClick={handleCreate} className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none py-5" data-testid="submit-movement">Create Movement</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProcurementCenter;
